/**
 * Status codes Sentry's `httpClientIntegration` auto-captures — shared by the
 * native (`App.tsx`) and web (`App.web.tsx`) Sentry setup so the two ranges
 * cannot drift apart (TA-10842).
 *
 * The array holds single codes and inclusive `[begin, end]` tuples.
 * Sentry's default is `[[500, 599]]`; we widen it to most 4xx/5xx but carve
 * out two handled cases:
 *  - 401 — recoverable ones are breadcrumbs; unrecoverable ones are captured
 *    explicitly in `errorLink`, so auto-capture would double-report.
 *  - 429 — the backend throttle working as designed; `errorLink` surfaces it
 *    to the user directly, so auto-capturing it is pure noise.
 */
export const SENTRY_FAILED_REQUEST_STATUS_CODES: (number | [number, number])[] =
  [400, [402, 428], [430, 599]];

/**
 * Whether `status` falls inside SENTRY_FAILED_REQUEST_STATUS_CODES — the same
 * membership test Sentry applies internally. Exported so a unit test can pin
 * the off-by-one boundaries (429 excluded, 428/430 still captured) without
 * booting Sentry.
 */
export const isSentryCapturedStatus = (status: number): boolean =>
  SENTRY_FAILED_REQUEST_STATUS_CODES.some((entry) =>
    typeof entry === 'number'
      ? entry === status
      : status >= entry[0] && status <= entry[1]
  );
